// /en/visa/$nationality — EN mirror of /visa/$nationality (P-EN-Visa-Nationality)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { VISA_NATIONALITIES, getVisaNationality, getVisaType } from "@/data/visa-nationalities";
import { VISA_TYPES } from "@/data/visa-types";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, dynamicOgImage, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import VisaNationalityTripleKeyLinks from "@/components/VisaNationalityTripleKeyLinks";
import { Plane, ArrowRight, ShieldCheck, MessageCircle, Phone, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/visa/$nationality/")({
  loader: ({ params }) => {
    if (!getVisaNationality(params.nationality)) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getVisaNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("Nationality not found", "/en/visa/$nationality", "en")}] };
    const enPath = `/en/visa/${n.slug}`;
    const thPath = `/visa/${n.slug}`;
    const url = `${SITE}${enPath}`;
    const title = `Thai Visa for ${n.demonymEn}s ${n.flag} · Non-B / LTR / DTV / Elite | NYC`;
    const desc = `Thai visa services for ${n.demonymEn} citizens (${n.countryEn}) — Non-B, LTR, DTV, Elite, Retirement, Marriage, ED. ${n.visaExempt > 0 ? `Visa-exempt ${n.visaExempt} days.` : "Requires advance application."} End-to-end filing with embassy & immigration.`;
    const og = dynamicOgImage({ title: `Thai Visa → ${n.demonymEn}s`, subtitle: `${n.countryEn} · ${n.topVisaTypes.length}+ visa types`, badge: "NYC Legal & Notary" });
    return {
      meta: [
        { title: ctaTitle(title, "/en/visa/$nationality", "en") }, { name: "description", content: ctaDesc(desc, "/en/visa/$nationality", "en")},
        { name: "keywords", content: `Thai visa ${n.demonymEn}, ${n.demonymEn} Non-B visa, ${n.demonymEn} LTR visa, ${n.demonymEn} DTV visa Thailand` },
        { property: "og:title", content: ctaTitle(title, "/en/visa/$nationality", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/visa/$nationality", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { property: "og:image", content: og }, { name: "twitter:image", content: og },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(thPath, enPath),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `Thai Visa Service for ${n.demonymEn} citizens`,
              serviceType: "Visa Application Service",
              inLanguage: "en",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: n.countryEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              offers: { "@type": "Offer", priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "HowTo", "@id": `${url}#howto`,
              name: `How to apply for a Thai visa as a ${n.demonymEn} citizen`,
              inLanguage: "en", totalTime: "P10D",
              step: [
                { "@type": "HowToStep", position: 1, name: "Choose visa type", text: `Pick from ${n.topVisaTypes.length}+ visa types suited for ${n.demonymEn} citizens (Non-B / LTR / DTV / Elite / Retirement / Marriage / ED).` },
                { "@type": "HowToStep", position: 2, name: "Prepare home-country documents", text: `Gather documents from ${n.countryEn} — Police Clearance, Bank Statement, Employment Letter.` },
                { "@type": "HowToStep", position: 3, name: "Certified translation", text: "MoJ-registered translator translates documents to Thai with sworn certification." },
                { "@type": "HowToStep", position: 4, name: "MFA Thailand / Apostille", text: "Submit at the Department of Consular Affairs — Apostille (since 2024) or standard legalization." },
                { "@type": "HowToStep", position: 5, name: "File at Embassy / Immigration", text: "We file at the Royal Thai Embassy or Chaengwattana Immigration on your behalf." },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc,
              hasPart: n.topVisaTypes.map((vt) => {
                const v = getVisaType(vt);
                return v ? { "@type": "WebPage", name: `${v.code || v.id} visa for ${n.demonymEn}s`, url: `${SITE}/visa/${n.slug}/${vt}` } : null;
              }).filter(Boolean),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Thai Visa", item: `${SITE}/en/visa` },
                { "@type": "ListItem", position: 3, name: `Thai Visa for ${n.demonymEn}s`, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: EnVisaNationality,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Nationality not found</h1>
      <Link to="/en/visa" className="mt-4 inline-block text-gold underline">Back to all visas</Link>
    </main>
  ),
});

function EnVisaNationality() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getVisaNationality(slug)!;
  const types = n.topVisaTypes.map((id) => VISA_TYPES.find((v) => v.id === id)).filter(Boolean) as typeof VISA_TYPES;
  const others = VISA_NATIONALITIES.filter((x) => x.slug !== slug && x.region === n.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/visa" className="hover:text-gold">Thai Visa</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{n.demonymEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> Thai Visa · {n.countryEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>
            Thai Visa for {n.demonymEn} citizens
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {n.visaExempt > 0
              ? `${n.demonymEn} passport holders enter Thailand visa-exempt for up to ${n.visaExempt} days. For longer stays, file Non-B / LTR / DTV / Elite / Retirement / Marriage / ED at the Royal Thai Embassy or Chaengwattana Immigration.`
              : `${n.demonymEn} passport holders must obtain a visa before travel. We handle Non-B / LTR / DTV / Elite / Retirement / Marriage / ED filings end-to-end.`}
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Country", v: n.countryEn },
              { k: "Visa-Exempt", v: n.visaExempt > 0 ? `${n.visaExempt} days` : "Not eligible" },
              { k: "Embassy filing", v: n.needsEmbassy ? "Required" : "Optional" },
              { k: "Visa types", v: `${n.topVisaTypes.length}+ available` },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC Legal & Notary Service (DBD 0435567000061, est. 2010) handles every Thai visa category for {n.demonymEn} citizens — {types.length} visa types including Non-B, LTR, DTV, Thai Elite, Retirement, Marriage, ED. End-to-end filing at the Royal Thai Embassy in {n.countryEn} or Chaengwattana Immigration. Rated 4.9/5 over 2,147 reviews.
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Visa categories for {n.demonymEn} citizens</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {types.map((v) => (
              <Link key={v.id} to="/visa/$nationality/$visatype" params={{ nationality: n.slug, visatype: v.id }}
                className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                <div className="flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{v.code}</div>
                  <div className="mt-0.5 font-display text-base font-extrabold text-navy-deep group-hover:text-amber-600">{v.name}</div>
                  <div className="mt-1 text-[12px] text-slate-600">{v.tagline}</div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                    <span><ShieldCheck className="mr-1 inline h-3 w-3 text-emerald-600" />{v.durationInitial}</span>
                    <span>{v.serviceFee}</span>
                  </div>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 text-slate-400 group-hover:text-amber-600" />
              </Link>
            ))}
          </div>
        </section>

        {others.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Nearby nationalities</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link key={o.slug} to="/en/visa/$nationality" params={{ nationality: o.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.demonymEn}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{o.countryEn}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <VisaNationalityTripleKeyLinks
          mode="en"
          nationalityNameTh={`ชาว${n.nameTh}`}
          nationalityDemonymEn={n.demonymEn}
          countryEn={n.countryEn}
        />

        <section className="mt-14 border-t border-slate-200 pt-10 text-sm text-slate-600">
          Looking for the Thai version? <Link to="/visa/$nationality" params={{ nationality: slug }} className="font-semibold text-amber-700 underline">/visa/{slug}</Link>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
